import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Typography,
  Box,
  Divider,
  SelectChangeEvent,
} from '@mui/material';

import { useUpdatePage } from '../../data/canvas/usePages';
import { useDatastores } from '../../data/canvas/useDatastores';
import { Page, UpdatePageRequest, Canvas } from '../../types/canvas.types';

interface PageConfigDialogProps {
  open: boolean;
  onClose: () => void;
  page: Page;
  canvas: Canvas;
  accountId: string;
}

export const PageConfigDialog: React.FC<PageConfigDialogProps> = ({
  open,
  onClose,
  page,
  canvas,
  accountId,
}) => {
  const [title, setTitle] = useState(page.title || '');
  const [description, setDescription] = useState(
    (page.settings && page.settings['description']) || '',
  );
  const [datastoreId, setDatastoreId] = useState(page.datastore_id || '');

  const updatePageMutation = useUpdatePage(page.id, canvas.id, accountId);
  const { data: datastores, isLoading: datastoresLoading } =
    useDatastores(accountId);

  // Reset form when page changes
  useEffect(() => {
    setTitle(page.title || '');
    setDescription((page.settings && page.settings['description']) || '');
    setDatastoreId(page.datastore_id || '');
  }, [page]);

  const handleSave = async () => {
    const updateData: UpdatePageRequest = {
      title,
      settings: {
        ...page.settings,
        description,
      },
      datastore_id: datastoreId || undefined,
    };

    try {
      await updatePageMutation.mutateAsync(updateData);
      onClose();
    } catch (error) {
      console.error('Failed to update page:', error);
    }
  };

  const handleClose = () => {
    // Reset form to original values on cancel
    setTitle(page.title || '');
    setDescription((page.settings && page.settings['description']) || '');
    setDatastoreId(page.datastore_id || '');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Page Settings: {page.title}</DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Basic Configuration */}
          <Typography variant="h6" gutterBottom>
            Basic Information
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Page Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter page title"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description (Optional)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter page description"
                multiline
                rows={3}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Datastore Configuration */}
          <Typography variant="h6" gutterBottom>
            Datastore Override
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Override the canvas default datastore for widgets on this page.
            {canvas.datastore_id && (
              <>
                {' '}
                Canvas default:{' '}
                <strong>
                  {datastores?.find((ds) => ds.id === canvas.datastore_id)
                    ?.name || canvas.datastore_id}
                </strong>
              </>
            )}
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth disabled={datastoresLoading}>
                <InputLabel>Datastore</InputLabel>
                <Select
                  value={datastoreId}
                  onChange={(e: SelectChangeEvent) =>
                    setDatastoreId(e.target.value)
                  }
                  label="Datastore"
                >
                  <MenuItem value="">
                    <em>Use canvas default</em>
                  </MenuItem>
                  {datastores?.map((ds) => (
                    <MenuItem key={ds.id} value={ds.id}>
                      {ds.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {updatePageMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Failed to update page. Please try again.
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={updatePageMutation.isPending}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={updatePageMutation.isPending || !title.trim()}
        >
          {updatePageMutation.isPending ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
